import { useState, useCallback, useRef, useEffect } from 'react'
import { Card, Typography, Layout, Space, theme, Button, Tag } from 'antd'
import { RocketOutlined, CheckOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { Bubble, Sender } from '@ant-design/x'
import { onboardingQuestions } from '@/data/onboardingQuestions'
import { useMutation } from '@tanstack/react-query'
import { apiService } from '@/lib/api'

const { Title, Text } = Typography
const { Content } = Layout

interface CompanyData {
  [key: string]: any
}

interface AnswerRecord {
  question_id: string
  question: string
  field: string
  type: string
  answer: string | string[]
  answer_label?: string
  answer_labels?: string[]
  answered_at: string
}

export default function OnboardingWizard() {
  const [companyData, setCompanyData] = useState<CompanyData>({})
  const [answersHistory, setAnswersHistory] = useState<AnswerRecord[]>([])
  const [startTime, setStartTime] = useState<string>(new Date().toISOString())
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [showOptions, setShowOptions] = useState(true)
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const [messages, setMessages] = useState<Array<{
    id: string
    content: string
    role: 'ai' | 'local'
    status: 'success'
  }>>([{
    id: 'welcome',
    content: onboardingQuestions[0].message,
    role: 'ai',
    status: 'success'
  }])

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  const handleAnswer = useCallback((answer: string | string[], displayText: string, answerLabel?: string | string[]) => {
    setInputValue('')
    setSelectedOptions([])
    setShowOptions(false)
    setMessages(prev => [...prev, { id: `user-${Date.now()}`, content: displayText, role: 'local', status: 'success' }])

    const currentQuestion = onboardingQuestions[currentQuestionIndex]
    setCompanyData(prev => ({ ...prev, [currentQuestion.field]: answer }))

    const answerRecord: AnswerRecord = {
      question_id: currentQuestion.id,
      question: currentQuestion.message,
      field: currentQuestion.field,
      type: currentQuestion.type,
      answer: answer,
      answered_at: new Date().toISOString()
    }

    if (Array.isArray(answerLabel)) {
      answerRecord.answer_labels = answerLabel
    } else if (answerLabel) {
      answerRecord.answer_label = answerLabel
    }

    setAnswersHistory(prev => [...prev, answerRecord])

    setTimeout(() => {
      if (currentQuestionIndex < onboardingQuestions.length - 1) {
        const nextIndex = currentQuestionIndex + 1
        setMessages(prev => [...prev, { id: `ai-${Date.now()}`, content: onboardingQuestions[nextIndex].message, role: 'ai', status: 'success' }])
        setCurrentQuestionIndex(nextIndex)
        setShowOptions(true)
      } else {
        setMessages(prev => [...prev, { id: 'complete', content: "🎉 Perfect! I've gathered all the information needed. Your accounting system will be configured based on your responses!", role: 'ai', status: 'success' }])
        setIsComplete(true)
      }
    }, 1000)
  }, [currentQuestionIndex])

  const handleOptionClick = (option: { label: string; value: string }) => {
    const currentQuestion = onboardingQuestions[currentQuestionIndex]

    if (currentQuestion.type === 'multi-select') {
      const newSelected = selectedOptions.includes(option.value)
        ? selectedOptions.filter(v => v !== option.value)
        : [...selectedOptions, option.value]
      setSelectedOptions(newSelected)

      if (newSelected.length > 0) {
        const selectedLabels = newSelected.map(v => currentQuestion.options?.find(opt => opt.value === v)?.label || '')
        const displayText = selectedLabels.join(', ')
        handleAnswer(newSelected, displayText, selectedLabels)
      }
    } else {
      handleAnswer(option.value, option.label, option.label)
    }
  }

  const handleInputSubmit = (content: string) => {
    handleAnswer(content, content)
  }

  const completeOnboardingMutation = useMutation({
    mutationFn: (payload: Parameters<typeof apiService.completeOnboarding>[0]) =>
      apiService.completeOnboarding(payload),
    onSuccess: (response) => {
      console.log('Onboarding completed successfully:', response)
      navigate('/coa-review')
    },
    onError: (error) => {
      console.error('Error completing onboarding:', error)
      navigate('/coa-review')
    }
  })

  const handleCompleteOnboarding = () => {
    const companyName = companyData.companyName || 'Unnamed Company'

    const payload = {
      company_name: companyName,
      onboarding_answers: {
        answers: answersHistory,
        metadata: {
          completed: true,
          completed_at: new Date().toISOString(),
          started_at: startTime,
          version: "1.0",
          total_questions: onboardingQuestions.length,
          answered_questions: answersHistory.length
        }
      }
    }

    completeOnboardingMutation.mutate(payload)
  }

  const currentQuestion = onboardingQuestions[currentQuestionIndex]

  const aiRole = { placement: 'start' as const, avatar: { src: 'https://mdn.alipayobjects.com/huamei_iwk9zp/afts/img/A*s5sNRo5LjfQAAAAAAAAAAAAADgCCAQ/fmt.webp', style: { backgroundColor: token.colorPrimaryBg } }, styles: { content: { backgroundColor: token.colorFillSecondary, color: token.colorText } } }
  const userRole = { placement: 'end' as const, avatar: { icon: '👤', style: { backgroundColor: token.colorSuccessBg } }, styles: { content: { backgroundColor: token.colorPrimary, color: token.colorWhite } } }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: token.colorBgContainer }}>
      <div style={{ background: token.colorBgContainer, borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: '24px 0' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px' }}>
          <Space size="middle">
            <div style={{ width: 40, height: 40, backgroundColor: token.colorPrimaryBg, borderRadius: token.borderRadius, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RocketOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            </div>
            <div>
              <Title level={2} style={{ margin: 0, color: token.colorText }}>AI Business Setup Assistant</Title>
              <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>Let our AI help you configure your accounting system</Text>
            </div>
          </Space>
        </div>
      </div>
      <Content style={{ padding: '24px', backgroundColor: token.colorBgLayout, display: 'flex', flexDirection: 'column' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Card style={{ borderRadius: token.borderRadiusLG, boxShadow: token.boxShadow, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div ref={chatContainerRef} style={{ flex: 1, padding: '24px', overflowY: 'auto', maxHeight: '60vh', scrollBehavior: 'smooth' }}>
              <Bubble.List items={messages} style={{ backgroundColor: 'transparent' }} roles={{ ai: aiRole, local: userRole }} />
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {isComplete ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
                  <Text style={{ color: token.colorTextSecondary }}>Setup complete! Question {currentQuestionIndex + 1} of {onboardingQuestions.length}</Text>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    onClick={handleCompleteOnboarding}
                    loading={completeOnboardingMutation.isPending}
                  >
                    Continue to Dashboard
                  </Button>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: 8, textAlign: 'center' }}>
                    <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>Question {currentQuestionIndex + 1} of {onboardingQuestions.length}</Text>
                  </div>
                  {currentQuestion.type !== 'input' && currentQuestion.options && showOptions && (
                    <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' }}>
                      {currentQuestion.options.map((option) => (
                        <Tag
                          key={option.value}
                          color={selectedOptions.includes(option.value) ? 'blue' : 'default'}
                          style={{
                            cursor: 'pointer',
                            borderRadius: 16,
                            fontSize: 12,
                            padding: '4px 12px'
                          }}
                          onClick={() => handleOptionClick(option)}
                        >
                          {option.label}
                        </Tag>
                      ))}
                    </div>
                  )}
                  <Sender onSubmit={handleInputSubmit} placeholder="Type your answer here..." style={{ backgroundColor: token.colorBgContainer }} value={inputValue} onChange={setInputValue} />
                </div>
              )}
            </div>
          </Card>
        </div>
      </Content>
    </Layout>
  )
}
